import React from 'react';
import { useTranslation } from 'react-i18next';
import { RatioWidgetProps } from 'types/WidgetTypes';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCaretDown, faCaretUp } from '@fortawesome/free-solid-svg-icons';
import { sumWidgetValues } from 'components/widgets/widgetDataUtils';
import '../../../style/scss/widgets.scss';

const RatioWidget: React.FC<RatioWidgetProps> = ({
    widgetKey, defaultData, dimensionData, measuringUnit, period, displayAsPercentage = false, showComparison = false
}) => {
    const { t } = useTranslation();

    // Count or sum the numerator and denominator from the current period data,
    // depending on whether the endpoint returned plain ids or id-value pairs
    const numerator = sumWidgetValues(defaultData.current);
    const denominator = dimensionData ? sumWidgetValues(dimensionData.current) : 0;

    // Count or sum the numerator and denominator from the previous period data
    const previousNumerator = sumWidgetValues(defaultData.previous);
    const previousDenominator = dimensionData ? sumWidgetValues(dimensionData.previous) : 0;

    // Turn a numerator/denominator pair into a rounded percentage
    const toPercentage = (numeratorCount: number, denominatorCount: number) =>
        denominatorCount !== 0 ? Math.round((numeratorCount / denominatorCount) * 100) : 0;

    const currentPercentage = toPercentage(numerator, denominator);
    const previousPercentage = toPercentage(previousNumerator, previousDenominator);
    const percentageDifference = currentPercentage - previousPercentage;

    // Determine the right period translation label based on the given period of the widget
    const periodLabel = period === 'week' ? 'date_time.general.week' : 'date_time.general.month';

    return (
        <div key={widgetKey} className='ratio-widget'>
            <div className='current-ratio'>
                {displayAsPercentage ? (
                    <h1>{currentPercentage}%</h1>
                ) : (
                    <>
                        <h1>{numerator}</h1>
                        <span className='ratio-total'>
                            {t('widget.general.ratio_total', { total: denominator, unit: measuringUnit ? t(measuringUnit) : '' })}
                        </span>
                    </>
                )}
            </div>
            {showComparison && (
                percentageDifference === 0 ? (
                    // If no difference, just show a dash
                    <div className='difference'>
                        <span>-</span>
                        &nbsp;{t('widget.general.difference_label', { period: t(periodLabel) })}
                    </div>
                ) : (
                    // If difference, show positive / negative caret icon with the difference
                    <div className='difference'>
                        <div className={percentageDifference > 0 ? 'positive' : 'negative'}>
                            <FontAwesomeIcon icon={percentageDifference > 0 ? faCaretUp : faCaretDown} />
                            <span> {Math.abs(percentageDifference)}%</span>
                        </div>
                        &nbsp;{t('widget.general.difference_label', { period: t(periodLabel) })}
                    </div>
                )
            )}
        </div>
    );
};

export default RatioWidget;
